import dayjs from "dayjs";
import { EAppointmentStatus } from "src/constants/appointments";

export enum EPaymentCollectionOption {
    REQUEST_PAYMENT = "REQUEST_PAYMENT",
    AUTOCHARGE = "AUTOCHARGE",
    SEND_TO_READER = "SEND_TO_READER",
}

export enum ECareResourceType {
    SERVICE = "SERVICE",
    CONSUMABLE = "CONSUMABLE",
    EQUIPMENT = "EQUIPMENT",
}

export enum ESubscriptionRecurrenceType {
    SUBSCRIPTION = "SUBSCRIPTION",
    FINITE = "FINITE",
}

export enum ESubscriptionRecurrenceFrequencyUnit {
    DAILY = "DAILY",
    WEEKLY = "WEEKLY",
    MONTHLY = "MONTHLY",
    YEARLY = "YEARLY",
}

export enum ERecurringPaymentStatus {
    ACTIVE = "ACTIVE",
    DRAFT = "DRAFT",
    // PAUSED = "PAUSED",
    CANCELLED = "CANCELLED",
    COMPLETED = "COMPLETED",
    OVERDUE = "OVERDUE",
    FAILED = "FAILED",
    PENDING = "PENDING",
}

export enum EPaymentAgingStatus {
    days1to15 = "1-15 days",
    days16to30 = "16-30 days",
    days31toInfinity = "31+ days",
    current = "Current",
}

export enum ECollectionOption {
    SEND_TO_READER = "sendToReader",
    CHARGE_CREDIT_CARD = "chargeCreditCard",
    SEND_INVOICE = "sendInvoice",
}

export function calculateAging(dueOn: string, paidOn: string | undefined | null) {
    if (paidOn) {
        return EPaymentAgingStatus.current;
    }
    const currentDate = dayjs();
    const dueDate = dayjs(dueOn);
    const diffDays = currentDate.diff(dueDate, "day");
    if (diffDays <= 15) {
        return EPaymentAgingStatus.days1to15;
    }
    if (diffDays <= 30) {
        return EPaymentAgingStatus.days16to30;
    }
    return EPaymentAgingStatus.days31toInfinity;
}

export enum EDueDateRange {
    Next24 = "Next24",
    TodayTomorrow = "TodayTomorrow",
    ThisWeek = "ThisWeek",
    NextWeek = "NextWeek",
    CompletedToday = "CompletedToday",
    CompletedLastWeek = "CompletedLastWeek",
    CompletedThisWeek = "CompletedThisWeek",
    Today = "Today",
    All = "All",
    Custom = "Custom",
}

export enum ECreatedDateRange {
    Last7Days = "Last7Days",
    Last30Days = "Last30Days",
    Last90Days = "Last90Days",
    YearToDate = "YearToDate",
    Today = "Today",
    Custom = "Custom",
}

// Not sure I like us overloading with both types, but keeping consistent
export const getUnixTimestampsForDateRange = (
    dateRange: ECreatedDateRange | EDueDateRange,
    timeZone: string,
) => {
    const now = dayjs().tz(timeZone);
    let start: dayjs.Dayjs;
    let end: dayjs.Dayjs;

    switch (dateRange) {
        case ECreatedDateRange.Last7Days:
            start = now.subtract(7, "day").startOf("day");
            end = now.endOf("day");
            break;
        case ECreatedDateRange.Last30Days:
            start = now.subtract(30, "day").startOf("day");
            end = now.endOf("day");
            break;
        case ECreatedDateRange.Last90Days:
            start = now.subtract(90, "day").startOf("day");
            end = now.endOf("day");
            break;
        case ECreatedDateRange.YearToDate:
            start = now.startOf("year");
            end = now.endOf("day");
            break;
        case ECreatedDateRange.Today:
        case EDueDateRange.Today:
            start = now.startOf("day");
            end = now.endOf("day");
            break;
        case EDueDateRange.Next24:
            start = now;
            end = now.add(1, "day");
            break;
        case EDueDateRange.TodayTomorrow:
            start = now.startOf("day");
            end = now.add(1, "day").endOf("day");
            break;
        case EDueDateRange.ThisWeek:
            start = now.startOf("week");
            end = now.endOf("week");
            break;
        case EDueDateRange.NextWeek:
            start = now.add(1, "week").startOf("week");
            end = now.add(1, "week").endOf("week");
            break;
        case EDueDateRange.CompletedToday:
            start = now.startOf("day");
            end = now.endOf("day");
            break;
        case EDueDateRange.CompletedLastWeek:
            start = now.subtract(1, "week").startOf("week");
            end = now.subtract(1, "week").endOf("week");
            break;
        case EDueDateRange.CompletedThisWeek:
            start = now.startOf("week");
            end = now.endOf("week");
            break;
        default:
            start = now.startOf("month");
            end = now.endOf("month");
    }

    return { startUnix: start.valueOf(), endUnix: end.valueOf() };
};

export const SELECT_ALL_OPTION = { value: "all_select_options", label: "All" };

export enum EInvoiceStatus {
    DRAFT = "DRAFT",
    OPEN = "OPEN",
    VIEWED = "VIEWED",
    PAID = "PAID",
    FAILED = "FAILED",
    PAST_DUE = "PAST_DUE",
    REFUNDED = "REFUNDED",
    UNCOLLECTIBLE = "UNCOLLECTIBLE",
    VOID = "VOID",
}

export enum EStaffPatientPortalViews {
    DEMOGRAPHICS = "demographics",
    APPOINTMENTS = "appointments",
    FORMS = "forms",
    BILLING = "billing",
    LABS = "labs",
    PRESCRIPTIONS = "prescriptions",
    DOCUMENTS = "documents",
    HEALTH_PROFILE = "health-profile",
    LOG_TASKS = "logTasks",
}

export enum EPaymentSettingsType {
    invoicesDashboard = 1,
    subscriptionsDashboard,
    paymentRequestsDashboard,
    paymentPayouts,
    paymentSettings,
    paymentCreateRequest,
    paymentRequestOnboarding,
    paymentRequestDetails,
}

export enum EPaymentRequestStatus {
    DRAFT = "DRAFT",
    OPEN = "OPEN",
    VIEWED = "VIEWED",
    PAID = "PAID",
    VOID = "VOID",
    UNCOLLECTIBLE = "UNCOLLECTIBLE",
    REFUNDED = "REFUNDED",
    PAST_DUE = "PAST_DUE",
    FAILED = "FAILED",
}

export const getPaymentRequestStatuses = (
    baseStatuses: EPaymentRequestStatus[],
    dueDateISO?: string | null,
): EPaymentRequestStatus[] => {
    const statuses = [...baseStatuses];
    const isVoid = statuses.includes(EPaymentRequestStatus.VOID);
    const isUncollectible = statuses.includes(EPaymentRequestStatus.UNCOLLECTIBLE);
    const isPaid = statuses.includes(EPaymentRequestStatus.PAID);
    // Only add PAST_DUE if:
    // 1. There is a due date
    // 2. Due date is in the past
    // 3. Payment is not void
    // 4. Payment is not uncollectible
    // 5. Payment is not paid
    if (
        dueDateISO &&
        dayjs.utc(dueDateISO).endOf("day").isBefore(dayjs.utc()) &&
        !isVoid &&
        !isUncollectible &&
        !isPaid
    ) {
        statuses.push(EPaymentRequestStatus.PAST_DUE);
    }

    return statuses;
};

export const EAppointmentLabels = {
    [EAppointmentStatus.SCHEDULED]: "Scheduled",
    [EAppointmentStatus.CANCELLED]: "Cancelled",
    [EAppointmentStatus.NO_SHOW]: "No-Show",
    [EAppointmentStatus.CHECKED_IN]: "Checked In",
    [EAppointmentStatus.IN_TREATMENT]: "In Progress",
    [EAppointmentStatus.CHECKED_OUT]: "Checked Out", // Also Completed?
    [EAppointmentStatus.CONFIRMED]: "Confirmed",
    [EAppointmentStatus.RESCHEDULED]: "Rescheduled",
};

export const STAFF_APPT_STATUS_SELECT_OPTIONS = [
    // staff can't specify confirmed or rescheduled
    EAppointmentStatus.SCHEDULED,
    EAppointmentStatus.CHECKED_IN,
    EAppointmentStatus.IN_TREATMENT,
    EAppointmentStatus.CHECKED_OUT,
    EAppointmentStatus.NO_SHOW,
    EAppointmentStatus.CANCELLED,
];

export interface IApptStatusOption {
    value: string | number;
    label: string;
    currentStatus: EAppointmentStatus;
    newStatus: EAppointmentStatus | null;
    availableActions: EAppointmentStatus[];
}

export const getApptStatusColor = (
    status: EAppointmentStatus,
): { backgroundColor: string; color: string } => {
    switch (status) {
        case EAppointmentStatus.SCHEDULED:
            return { backgroundColor: "#d3dff8", color: "#215591" };
        case EAppointmentStatus.CHECKED_OUT:
            return { backgroundColor: "#d8efd8", color: "#016d02" };
        case EAppointmentStatus.IN_TREATMENT:
            return { backgroundColor: "#d6dbff", color: "#0027ca" };
        case EAppointmentStatus.CHECKED_IN:
            return { backgroundColor: "#fff3b2", color: "#623909" };
        case EAppointmentStatus.NO_SHOW:
            return { backgroundColor: "#f1cbca", color: "#9b0000" };
        case EAppointmentStatus.CANCELLED:
            return { backgroundColor: "#f2f2f2", color: "#494949" };
        default:
            return { backgroundColor: "#f2f2f2", color: "#494949" };
    }
};

export const getStaffApptStatusOptions = (): IApptStatusOption[] => {
    return STAFF_APPT_STATUS_SELECT_OPTIONS.map((status) => {
        return {
            value: status,
            label: EAppointmentLabels[status],
            currentStatus: status,
            newStatus: null,
            availableActions: [],
        };
    });
};

export enum ELabsVendorType {
    IN_HOUSE = "IN_HOUSE",
    ACCESS_MEDICAL = "ACCESS_MEDICAL",
    OTHER = "OTHER",
    LABCORP = "LABCORP",
}

export enum ELabFieldType {
    DATE = "DATE",
    TIME = "TIME",
    NUMBER = "NUMBER",
    NPI_SELECT = "NPI_SELECT",
}

export enum ELabsStatus {
    PROCESSING = "PROCESSING",
    COMPLETE = "COMPLETE",
    ORDER_ERROR = "ORDER_ERROR",
}

export enum EAppointmentRecurrenceType {
    DOESNT_REPEAT = "Does not repeat",
    WEEKLY = "weekly",
    // TODO: RECURRING_APPT_4 Temporarily removed it until there's a proper implementation and use case
    // MONTHLY = "monthly",
}

export enum EAppointmentSeriesUpdateType {
    THIS_APPOINTMENT = "This appointment",
    THIS_AND_FUTURE_APPOINTMENTS = "This & following appointments",
    ALL_APPOINTMENTS = "All appointments",
}

export enum EDaysOfWeek {
    MONDAY = "Monday",
    TUESDAY = "Tuesday",
    WEDNESDAY = "Wednesday",
    THURSDAY = "Thursday",
    FRIDAY = "Friday",
    SATURDAY = "Saturday",
    SUNDAY = "Sunday",
}

export enum ELabsConfigType {
    COMPONENT = "labTestComponentConfig",
    PANEL = "labTestPanelConfig",
    PACKET = "labTestPacketConfig",
}

export enum ETaskStatus {
    OPEN = "OPEN",
    COMPLETED = "COMPLETED",
    DELETED = "DELETED",
}

export enum ETaskType {
    LABS = "LABS",
    BILLING = "BILLING",
    PRESCRIPTION = "PRESCRIPTION",
    INTAKE = "INTAKE",
    SCHEDULING = "SCHEDULING",
    CSL = "CSL",
    CLINICAL_CHART = "CLINICAL_CHART",
    OTHER = "OTHER",
}

export enum ETaskAction {
    OTHER = "OTHER",
    VIEW_LABS = "VIEW_LABS",
    ORDER_LABS = "ORDER_LABS",
    SCHEDULE_APPOINTMENT = "SCHEDULE_APPOINTMENT",
    VIEW_FORMS = "VIEW_FORMS",
    PRESCRIBE_MEDICATION = "PRESCRIBE_MEDICATION",
    SIGN_SIGNATURE_REQUEST = "SIGN_SIGNATURE_REQUEST",
    RETURN_CLINICAL_CHART = "RETURN_CLINICAL_CHART",
}

export enum EMigrationType {
    PATIENT = "patient",
    APPOINTMENT = "appointment",
    DOCUMENT = "document",
    CLINICAL_APPOINTMENT_NOTE = "clinical_appointment_note",
    LAB_DOCUMENT = "lab_document",
    PATIENT_CURSOR = "patient_cursor",
}

export enum EMigrationStatus {
    EXISTING_HARD_MATCH = "EXISTING_HARD_MATCH",
    EXISTING_SOFT_MATCH = "EXISTING_SOFT_MATCH",
    EMAIL_OR_PHONE_MATCH = "EMAIL_OR_PHONE_MATCH",
    NAME_ONLY_MATCH = "NAME_ONLY_MATCH",
    CREATE = "CREATE",
    NEEDS_DETAILS = "NEEDS_DETAILS",
    FAILED = "FAILED",
    IGNORED = "IGNORED",
    SKIPPED = "SKIPPED",
}

export const MIGRATION_STATUS_COLORS = {
    [EMigrationStatus.EXISTING_HARD_MATCH]: "primary",
    [EMigrationStatus.EXISTING_SOFT_MATCH]: "info",
    [EMigrationStatus.EMAIL_OR_PHONE_MATCH]: "info",
    [EMigrationStatus.NAME_ONLY_MATCH]: "info",
    [EMigrationStatus.CREATE]: "success",
    [EMigrationStatus.NEEDS_DETAILS]: "warning",
    [EMigrationStatus.FAILED]: "danger",
    [EMigrationStatus.IGNORED]: "secondary",
    [EMigrationStatus.SKIPPED]: "light text-muted",
};

export enum ECSLContainerStatus {
    OPEN = "Open",
    AVAILABLE = "Available",
    EMPTY = "Empty",
    FINISHED = "Finished",
    DISPOSED = "Disposed", // Not an actual status in the backend, but used for display purposes
}

export const ECSL_STATUS_COLORS = {
    [ECSLContainerStatus.OPEN]: "success",
    [ECSLContainerStatus.AVAILABLE]: "primary",
    [ECSLContainerStatus.EMPTY]: "danger",
};

export const getCSLContainerStatusColors = (
    status: string,
): { backgroundColor: string; color: string } => {
    const eStatus = status as ECSLContainerStatus;
    switch (eStatus) {
        case ECSLContainerStatus.AVAILABLE:
            return { backgroundColor: "#d3dff8", color: "#215591" };
        case ECSLContainerStatus.OPEN:
            return { backgroundColor: "#d8efd8", color: "#016d02" };
        case ECSLContainerStatus.EMPTY:
            return { backgroundColor: "#f1cbca", color: "#9b0000" };
        case ECSLContainerStatus.FINISHED:
            return { backgroundColor: "#f5f5f5", color: "#000000" };
        case ECSLContainerStatus.DISPOSED:
            return { backgroundColor: "#f2f2f2", color: "#494949" };
        default:
            return { backgroundColor: "#f2f2f2", color: "#494949" };
    }
};

export enum ECSLLogType {
    ENTRY = "Entry",
    ADMINISTERING = "Administering",
    DISPENSING = "Dispensing",
    WASTING = "Wasting",
    ADJUSTMENT = "Adjustment",
    DISPOSING = "Disposing",
    STATUS = "Status",
}

export function requiresWitnessSignature(logType?: ECSLLogType): boolean {
    if (!logType) return false;
    return (
        logType === ECSLLogType.WASTING ||
        logType === ECSLLogType.DISPOSING ||
        logType === ECSLLogType.ADJUSTMENT
    );
}

export enum ECSLLogOptionType {
    INJECTION_SITE = "InjectionSite",
    ADMINISTRATION_REASON = "AdministrationReason",
    DESTRUCTION_METHOD = "DestructionMethod",
    WASTING_REASON = "WastingReason",
    DISPENSING_REASON = "DispensingReason",
    DISPOSAL_METHOD = "DisposalMethod",
}

export const CSL_LOG_OPTION_LABELS = {
    [ECSLLogOptionType.INJECTION_SITE]: "Injection Site",
    [ECSLLogOptionType.ADMINISTRATION_REASON]: "Administration Reason",
    [ECSLLogOptionType.DESTRUCTION_METHOD]: "Destruction Method",
    [ECSLLogOptionType.WASTING_REASON]: "Wasting Reason",
    [ECSLLogOptionType.DISPENSING_REASON]: "Dispensing Reason",
    [ECSLLogOptionType.DISPOSAL_METHOD]: "Disposal Method",
};

export enum ECSLSignatureRequestType {
    PRIMARY = "Primary",
    WITNESS = "Witness",
}

export enum ECSLContainerLogStatus {
    COMPLETE = "Complete",
    PENDING = "Pending",
    NULLIFIED = "Nullified",
    CANCELLED = "Cancelled",
    RECONCILED = "Reconciled",
}

export const getReconciliableContainerLogStatuses = (): ECSLContainerLogStatus[] => [
    ECSLContainerLogStatus.COMPLETE,
    ECSLContainerLogStatus.NULLIFIED,
];

export const getExcludedReconciliableContainerLogTypes = (): ECSLLogType[] => [
    ECSLLogType.ENTRY,
    ECSLLogType.STATUS,
];

// TODO: CSL_4 utilize jaan's new coloring schema
export const getCSLLogTypeColors = (
    logType: ECSLLogType | string,
): { backgroundColor: string; color: string } => {
    const eLogType = (typeof logType === "string" ? logType : String(logType)) as ECSLLogType;

    switch (eLogType) {
        case ECSLLogType.ENTRY:
            return { backgroundColor: "#d8efd8", color: "#016d02" };
        case ECSLLogType.ADMINISTERING:
        case ECSLLogType.DISPENSING:
            return { backgroundColor: "#d3dff8", color: "#215591" };
        case ECSLLogType.WASTING:
            return { backgroundColor: "#f1cbca", color: "#9b0000" };
        case ECSLLogType.ADJUSTMENT:
            return { backgroundColor: "#fff3b2", color: "#623909" };
        case ECSLLogType.DISPOSING:
            return { backgroundColor: "#f2f2f2", color: "#494949" };
        default:
            return { backgroundColor: "#f2f2f2", color: "#494949" };
    }
};

export const getCSLLogTypeColor = (type: ECSLLogType): string => {
    switch (type) {
        case ECSLLogType.ENTRY:
            return "#016d02"; // Green
        case ECSLLogType.WASTING:
            return "#9b0000"; // Red
        case ECSLLogType.ADMINISTERING:
        case ECSLLogType.DISPENSING:
            return "#215591"; // Blue
        default:
            return "#494949"; // Gray
    }
};

export const getCSLContainerLogStatusColors = (
    status: ECSLContainerLogStatus,
): { backgroundColor: string; color: string } => {
    switch (status) {
        case ECSLContainerLogStatus.COMPLETE:
            return { backgroundColor: "#e6f4ea", color: "#1e4620" };
        case ECSLContainerLogStatus.RECONCILED:
            return { backgroundColor: "#d3dff8", color: "#215591" };
        case ECSLContainerLogStatus.PENDING:
            return { backgroundColor: "#fff3e0", color: "#885a00" };
        case ECSLContainerLogStatus.NULLIFIED:
            return { backgroundColor: "#fbe9e7", color: "#c41c00" };
        case ECSLContainerLogStatus.CANCELLED:
            return { backgroundColor: "#f5f5f5", color: "#666666" };
        default:
            return { backgroundColor: "#f5f5f5", color: "#666666" };
    }
};

export enum ECSLCheckoutStatus {
    AVAILABLE = "AVAILABLE",
    NO_CONTAINERS_AVAILABLE = "NO_CONTAINERS_AVAILABLE",
    ALREADY_CHECKED_OUT_TO_SELF = "ALREADY_CHECKED_OUT_TO_SELF",
    ALREADY_CHECKED_OUT_TO_OTHER = "ALREADY_CHECKED_OUT_TO_OTHER",
    TAKEOVER_FROM_OTHER = "TAKEOVER_FROM_OTHER",
    NO_TAKEOVER_ALLOWED = "NO_TAKEOVER_ALLOWED",
    SUCCESS = "SUCCESS",
    ERROR = "ERROR",
}

export enum EDeaSchedule {
    CI = "CI",
    CII = "CII",
    CIII = "CIII",
    CIV = "CIV",
    CV = "CV",
    NOT_SCHEDULED = "NOT_SCHEDULED",
}

export const DEA_SCHEDULE_OPTIONS = [
    { value: EDeaSchedule.CI, label: "Schedule I (CI) - No Medical Use" },
    { value: EDeaSchedule.CII, label: "Schedule II (CII) - High Potential for Abuse" },
    {
        value: EDeaSchedule.CIII,
        label: "Schedule III (CIII) - Moderate to Low Potential for Abuse",
    },
    { value: EDeaSchedule.CIV, label: "Schedule IV (CIV) - Low Potential for Abuse" },
    { value: EDeaSchedule.CV, label: "Schedule V (CV) - Lowest Potential for Abuse" },
    { value: EDeaSchedule.NOT_SCHEDULED, label: "Not Scheduled" },
];

export enum ESignatureRequestFlowType {
    CONTAINER_ENTRY = "CONTAINER_ENTRY",
    CONTAINER_LOG_ENTRY = "CONTAINER_LOG_ENTRY",
    CONTAINER_LOG_NULLIFICATION = "CONTAINER_LOG_NULLIFICATION",
}

export enum ESearchTableType {
    CSL_CURRENT_INVENTORY = "CSL_CURRENT_INVENTORY",
    CSL_CONTAINER_LOGS = "CSL_CONTAINER_LOGS",
    CSL_RECONCILIATION_LOGS = "CSL_RECONCILIATION_LOGS",
    CSL_CONTAINER_ARCHIVE = "CSL_CONTAINER_ARCHIVE",
    CLINICAL_CHARTS_DASHBOARD = "CLINICAL_CHARTS_DASHBOARD",
}

// Not sure we need a status...
export enum EReconciliationStatus {
    DRAFT = "Draft",
    COMPLETE = "Complete",
    DISCREPANCY = "Discrepancy",
}

// Must align with backend AccountCountryEnum
export enum EAccountCountry {
    USA = "USA",
    CANADA = "CANADA",
}
// Name displayed to user
export const ACCOUNT_COUNTRY_LABELS = {
    [EAccountCountry.USA]: "US",
    [EAccountCountry.CANADA]: "Canada",
};

export const PAYMENT_SETUP_REQUIRED = new Set<EAccountCountry>([EAccountCountry.USA]);

export enum EChartStatus {
    DRAFT = 0,
    FINALIZED = 1,
    ARCHIVED = 2,
    BACK_TO_PROVIDER = 3,
    SIGNED_OFF = 4,
}

export enum ELabsRangeStatus {
    BELOW_NORMAL = "BELOW_NORMAL",
    WITHIN_NORMAL = "WITHIN_NORMAL",
    ABOVE_NORMAL = "ABOVE_NORMAL",
}

export enum EIntegrationType {
    ACCOUNT_PATIENT = "accountPatient",
    APPOINTMENT = "appointment",
    APPOINTMENT_TYPE = "appointmentType",
    LOCATION = "location",
    STAFF = "staff",
    FILE_UPLOAD = "fileUpload",
    FORM_TEMPLATE = "formTemplate",
    FORM_TEMPLATE_LABEL = "formTemplateLabel",
}

export enum ECSLContainerLogTab {
    USAGE = "usage",
    STATUS = "status",
    DISPOSAL = "disposal",
}

export enum ECSLContainerLogBatchType {
    STANDARD = 0,
    RECONCILIATION = 1,
    NULLIFICATION = 2,
}
